import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// PrimeNG imports
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MenuModule } from 'primeng/menu';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PaginatorModule } from 'primeng/paginator';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';

import { MenuItem } from 'primeng/api';
import { CustomMessageService } from '../../../core/services/custom-message.service';
import { StaticReportService } from '../../../core/services/static-report.service';
import { AuthService } from '../../../core/services/auth';
import { SubscriptionContextService } from '../../../core/services/subscription-context.service';
import { UserService, User } from '../../../core/services/user.service';
import { ProductService, Product } from '../../../core/services/product.service';
import { STATIC_REPORTS_CONFIG } from '../static-reports.config';

@Component({
  selector: 'app-static-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    SelectModule,
    SplitButtonModule,
    MenuModule,
    ProgressSpinnerModule,
    PaginatorModule,
    CardModule,
    DatePickerModule
  ],
  providers: [
    CustomMessageService,
    StaticReportService,
    AuthService,
    SubscriptionContextService,
    UserService,
    ProductService
  ],
  templateUrl: './static-reports.component.html',
  styleUrl: './static-reports.component.scss'
})
export class StaticReportsComponent implements OnInit {
  selectedStaticReport: string | null = null;
  staticReportData: any = null;
  currentPageStatic = 1;
  pageSizeStatic = 10;
  totalRecordsStatic = 0;
  
  // Static report configurations - imported from shared config
  staticReports: any[] = STATIC_REPORTS_CONFIG;
  
  // Filters for static reports
  // Default to past month for leaderboard
  selectedMonth: number = (() => {
    const now = new Date();
    const pastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return pastMonth.getMonth() + 1;
  })();
  selectedYear: number = (() => {
    const now = new Date();
    const pastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return pastMonth.getFullYear();
  })();
  months: { label: string; value: number }[] = [];
  years: number[] = [];
  personalPipelineType: 'active' | 'expired' = 'active';
  monthlyWinLossType: 'wins' | 'losses' = 'wins';
  selectedDate: Date | null = null; // For Personal Pipeline daily report
  maxDate: Date = new Date(); // Maximum selectable date (today)

  // Current user for role checking
  currentUser: any = null;
  selectedSubscriberId: string | null = null;
  loading = false;
  initialLoading = true;
  exportMenuItems: MenuItem[] = [];
  availableReports: any[] = [];
  exportMenuVisible = false;
  
  // FSR dropdown for admin users
  fsrUsers: User[] = [];
  selectedFSRId: string | null = null;
  isAdmin: boolean = false;

  // Product dropdown for product report
  products: Product[] = [];
  selectedProductId: string | null = null;

  constructor(
    private staticReportService: StaticReportService,
    private authService: AuthService,
    private subscriptionContextService: SubscriptionContextService,
    private userService: UserService,
    private productService: ProductService,
    public customMessageService: CustomMessageService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.initialLoading = true;
    try {
      await this.loadCurrentUser();
      this.initFilters();
      this.initializeExportMenuItems();
      this.checkIfAdmin();
      await this.loadProducts();
    } finally {
      this.initialLoading = false;
    }
  }

  async loadCurrentUser() {
    try {
      const response = await this.authService.getCurrentUser().toPromise();
      // Extract user from response if it's wrapped
      this.currentUser = response?.user || response;
      console.log('Current user loaded:', this.currentUser);
      console.log('User roleId:', this.currentUser?.roleId);
      console.log('User roleId.identification:', this.currentUser?.roleId?.identification);
      // Update available reports after user loads
      this.updateAvailableReports();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  }

  initFilters() {
    // Initialize months
    this.months = [
      { label: 'January', value: 1 },
      { label: 'February', value: 2 },
      { label: 'March', value: 3 },
      { label: 'April', value: 4 },
      { label: 'May', value: 5 },
      { label: 'June', value: 6 },
      { label: 'July', value: 7 },
      { label: 'August', value: 8 },
      { label: 'September', value: 9 },
      { label: 'October', value: 10 },
      { label: 'November', value: 11 },
      { label: 'December', value: 12 }
    ];

    // Initialize years (current year ± 5 years)
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      this.years.push(i);
    }
  }

  checkIfAdmin() {
    if (!this.currentUser || !this.currentUser.roleId) {
      this.isAdmin = false;
      return;
    }
    
    const userRole = this.currentUser.roleId.identification || 'USER';
    const adminRoles = ['SUPER_DEV_ADMIN', 'SA', 'A', 'SM', 'CSM'];
    this.isAdmin = adminRoles.includes(userRole);
    
    if (this.isAdmin) {
      this.loadFSRUsers();
    }
  }

  async loadFSRUsers() {
    try {
      this.selectedSubscriberId = this.subscriptionContextService.getSelectedSubscriberId();
      if (!this.selectedSubscriberId) {
        return;
      }
      
      const allUsers = await this.userService.getUsersBySubscriber(this.selectedSubscriberId).toPromise();
      if (!allUsers) {
        this.fsrUsers = [];
        return;
      }
      
      const fsrList = allUsers.filter(user => user.roleId?.identification === 'FSR' && user.isActive && user.status === 'APPROVED');
      this.fsrUsers = fsrList.map(user => ({
        ...user,
        displayName: `${user.firstName} ${user.lastName}`
      }));
    } catch (error) {
      console.error('Error loading FSR users:', error);
      this.fsrUsers = [];
    }
  }

  onFSRChange() {
    if (this.selectedFSRId && this.selectedStaticReport) {
      this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  async loadProducts() {
    try {
      this.selectedSubscriberId = this.subscriptionContextService.getSelectedSubscriberId();
      if (!this.selectedSubscriberId) {
        return;
      }
      
      const response = await this.productService.getPagedProducts({
        pageIndex: 1,
        pageSize: 1000,
        filters: {
          isActive: true,
          isArchived: false
        }
      }).toPromise();
      
      if (response && response.data) {
        this.products = response.data;
      }
    } catch (error) {
      console.error('Error loading products:', error);
      this.products = [];
    }
  }

  onProductChange() {
    if (this.selectedStaticReport === 'product') {
      this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  hasAccessToReport(allowedRoles: string[]): boolean {
    // Get current user role
    if (!this.currentUser || !this.currentUser.roleId) {
      // Deny access if user context not loaded yet (secure by default)
      console.log('Permission check: User not loaded yet', { currentUser: this.currentUser });
      return false;
    }
    
    const userRole = this.currentUser.roleId.identification || 'USER';
    const hasAccess = allowedRoles.includes(userRole);
    console.log('Permission check:', { 
      userRole, 
      allowedRoles, 
      hasAccess,
      currentUser: this.currentUser,
      roleId: this.currentUser.roleId
    });
    return hasAccess;
  }

  updateAvailableReports() {
    this.availableReports = this.staticReports.filter(report => {
      const hasAccess = this.hasAccessToReport(report.allowedRoles);
      console.log(`Report "${report.name}" (${report.id}):`, { 
        allowedRoles: report.allowedRoles, 
        hasAccess 
      });
      return hasAccess;
    });
    console.log('Available static reports:', this.availableReports.length, 'out of', this.staticReports.length);
  }

  getAvailableStaticReports() {
    // Return cached available reports
    return this.availableReports;
  }

  async selectStaticReport(report: any) {
    console.log('selectStaticReport called:', report);
    if (!this.hasAccessToReport(report.allowedRoles)) {
      this.customMessageService.error('Access Denied', 'You do not have permission to access this report');
      return;
    }

    this.selectedStaticReport = report.id;
    this.customMessageService.clearAll();
    
    // Reload products if this is the product report
    if (report.id === 'product') {
      await this.loadProducts();
    }
    
    console.log('About to call loadStaticReportData with:', report.id);
    this.loadStaticReportData(report.id);
  }

  async loadStaticReportData(reportId: string) {
    console.log('loadStaticReportData called with reportId:', reportId);
    
    // Permission check - find the report and verify access
    const report = this.staticReports.find(r => r.id === reportId);
    if (!report) {
      this.customMessageService.error('Error', 'Report not found');
      return;
    }
    
    if (!this.hasAccessToReport(report.allowedRoles)) {
      this.customMessageService.error('Access Denied', 'You do not have permission to access this report');
      this.selectedStaticReport = null;
      this.staticReportData = null;
      return;
    }
    
    this.loading = true;
    this.selectedSubscriberId = this.subscriptionContextService.getSelectedSubscriberId();
    console.log('Loading started, selectedSubscriberId:', this.selectedSubscriberId);

    try {
      console.log('Entering switch for reportId:', reportId);
      switch (reportId) {
        case 'leaderboard':
          const leaderboardResult = await this.staticReportService.getLeaderboard(
            this.selectedMonth,
            this.selectedYear,
            this.currentPageStatic,
            this.pageSizeStatic,
            this.selectedSubscriberId || undefined
          );
          console.log('Leaderboard result:', leaderboardResult);
          // Service returns { data: LeaderboardData[]; total: number; pagination: any; period: any }
          this.staticReportData = leaderboardResult.data || leaderboardResult;
          this.totalRecordsStatic = leaderboardResult.total || (Array.isArray(leaderboardResult.data) ? leaderboardResult.data.length : (Array.isArray(leaderboardResult) ? leaderboardResult.length : 0));
          console.log('Static report data set:', this.staticReportData);
          break;

        case 'product':
          const productResult = await this.staticReportService.getProductReport(
            this.selectedSubscriberId || undefined,
            this.selectedProductId || undefined
          );
          console.log('Product Report result:', productResult);
          if (productResult && productResult.length > 0) {
            console.log('First product data:', productResult[0]);
            if (productResult[0].rollingMonths && productResult[0].rollingMonths.length > 0) {
              console.log('First month data:', productResult[0].rollingMonths[0]);
              console.log('First month valueOfContracts:', productResult[0].rollingMonths[0].valueOfContracts);
            }
          }
          this.staticReportData = productResult;
          break;
          
        case 'team-activity':
          const teamActivityResult = await this.staticReportService.getTeamActivity(
            this.currentPageStatic,
            this.pageSizeStatic,
            this.selectedSubscriberId || undefined
          );
          console.log('Team Activity result:', teamActivityResult);
          this.staticReportData = Array.isArray(teamActivityResult) ? teamActivityResult : teamActivityResult?.data;
          this.totalRecordsStatic = teamActivityResult?.total || (Array.isArray(teamActivityResult) ? teamActivityResult.length : 0);
          break;

        case 'pipeline-alert':
          const pipelineResult = await this.staticReportService.getPipelineAlert(
            this.currentPageStatic,
            this.pageSizeStatic,
            this.selectedSubscriberId || undefined
          );
          console.log('Pipeline Alert result:', pipelineResult);
          this.staticReportData = Array.isArray(pipelineResult) ? pipelineResult : pipelineResult?.data;
          this.totalRecordsStatic = pipelineResult?.total || (Array.isArray(pipelineResult) ? pipelineResult.length : 0);
          break;

        case 'fsr-performance':
          const startDate = new Date();
          startDate.setMonth(startDate.getMonth() - 3);
          const endDate = new Date();
          const fsrPerfResult = await this.staticReportService.getFSRTeamPerformance(
            startDate,
            endDate,
            this.currentPageStatic,
            this.pageSizeStatic,
            this.selectedSubscriberId || undefined
          );
          console.log('FSR Performance result:', fsrPerfResult);
          this.staticReportData = Array.isArray(fsrPerfResult) ? fsrPerfResult : fsrPerfResult?.data;
          this.totalRecordsStatic = fsrPerfResult?.total || (Array.isArray(fsrPerfResult) ? fsrPerfResult.length : 0);
          console.log('Static report data set for FSR:', this.staticReportData);
          break;

        case 'personal-pipeline':
          const pipelineTypeResult = await this.staticReportService.getPersonalPipeline(
            this.personalPipelineType,
            this.currentPageStatic,
            this.pageSizeStatic,
            this.selectedFSRId || undefined,
            this.selectedDate || undefined,
            this.selectedSubscriberId || undefined
          );
          console.log('Personal Pipeline result:', pipelineTypeResult);
          this.staticReportData = Array.isArray(pipelineTypeResult) ? pipelineTypeResult : pipelineTypeResult?.data;
          this.totalRecordsStatic = pipelineTypeResult?.total || (Array.isArray(pipelineTypeResult) ? pipelineTypeResult.length : 0);
          break;

        case 'monthly-winloss':
          const winLossResult = await this.staticReportService.getMonthlyWinLoss(
            this.selectedMonth,
            this.selectedYear,
            this.monthlyWinLossType,
            this.currentPageStatic,
            this.pageSizeStatic,
            this.selectedFSRId || undefined
          );
          console.log('Monthly Win/Loss result:', winLossResult);
          this.staticReportData = Array.isArray(winLossResult) ? winLossResult : winLossResult?.data;
          this.totalRecordsStatic = winLossResult?.total || (Array.isArray(winLossResult) ? winLossResult.length : 0);
          break;

        case 'personal-performance':
          const personalPerfResult = await this.staticReportService.getPersonalPerformance(
            undefined,
            undefined,
            this.selectedFSRId || undefined
          );
          this.staticReportData = personalPerfResult;
          break;
      }
      
      console.log('Loading finished. staticReportData:', this.staticReportData, 'loading:', this.loading);
    } catch (error) {
      console.error('Error loading static report data:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      this.customMessageService.error('Error', 'Failed to load report data');
      this.staticReportData = null;
    } finally {
      this.loading = false;
      console.log('Finally block: loading set to false, staticReportData:', this.staticReportData);
    }
  }

  async onPageChangeStatic(event: any) {
    this.currentPageStatic = event.page + 1;
    this.pageSizeStatic = event.rows;
    
    if (this.selectedStaticReport) {
      await this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  async refreshStaticReport() {
    if (this.selectedStaticReport) {
      await this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  exporting = false;

  async exportStaticReport(format: 'csv' | 'excel' | 'pdf') {
    if (!this.selectedStaticReport || !this.staticReportData) {
      this.customMessageService.warning('No Data', 'No data available to export');
      return;
    }

    this.exporting = true;
    this.customMessageService.info('Export in Progress', 'Please wait while the report is being exported...');

    try {
      const result = await this.staticReportService.exportReport(
        this.selectedStaticReport,
        format,
        this.getExportParams()
      ).toPromise();

      // Determine MIME type based on format
      let mimeType: string;
      let fileExtension: string;
      
      switch (format) {
        case 'csv':
          mimeType = 'text/csv;charset=utf-8;';
          fileExtension = 'csv';
          break;
        case 'excel':
          mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          fileExtension = 'xlsx';
          break;
        case 'pdf':
          mimeType = 'application/pdf';
          fileExtension = 'pdf';
          break;
        default:
          mimeType = 'application/octet-stream';
          fileExtension = format;
      }

      // Create blob with proper MIME type
      const blob = new Blob([result as Blob], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      
      // Generate filename in format: ESO_{reportName}_YYYY-MM_YYYYMMDD.{extension}
      // Use report ID as the identifier (e.g., "leaderboard", "product", "team-activity")
      const reportName = this.selectedStaticReport || 'report';
      
      // Get YYYY-MM from selected month/year if available, otherwise use current date
      let yearMonth: string;
      if ((this.selectedStaticReport === 'leaderboard' || this.selectedStaticReport === 'monthly-winloss') && 
          this.selectedMonth && this.selectedYear) {
        const monthStr = String(this.selectedMonth).padStart(2, '0');
        yearMonth = `${this.selectedYear}-${monthStr}`;
      } else {
        const now = new Date();
        const monthStr = String(now.getMonth() + 1).padStart(2, '0');
        yearMonth = `${now.getFullYear()}-${monthStr}`;
      }
      
      // Get YYYYMMDD from current date
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const dateStr = `${year}${month}${day}`;
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `ESO_${reportName}_${yearMonth}_${dateStr}.${fileExtension}`;
      
      // Append to body, click, then remove
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      this.customMessageService.success('Export Successful', 'Report exported successfully');
    } catch (error: any) {
      console.error('Error exporting report:', error);
      this.customMessageService.error('Export Failed', 'Export failed, please try again');
    } finally {
      this.exporting = false;
    }
  }

  getExportParams(): any {
    const params: any = {};
    
    if (this.selectedStaticReport === 'leaderboard' || this.selectedStaticReport === 'monthly-winloss') {
      params.month = this.selectedMonth;
      params.year = this.selectedYear;
    }
    
    if (this.selectedStaticReport === 'personal-pipeline') {
      params.type = this.personalPipelineType;
      if (this.selectedDate) {
        params.selectedDate = this.selectedDate.toISOString();
      }
    }
    
    if (this.selectedStaticReport === 'monthly-winloss') {
      params.type = this.monthlyWinLossType;
    }

    // Add subscriber ID for all reports
    if (this.selectedSubscriberId) {
      params.selectedSubscriberId = this.selectedSubscriberId;
    }

    // Add product ID for product report
    if (this.selectedStaticReport === 'product' && this.selectedProductId) {
      params.productId = this.selectedProductId;
    }

    // Add FSR ID for personal reports
    if ((this.selectedStaticReport === 'personal-pipeline' || 
         this.selectedStaticReport === 'monthly-winloss' || 
         this.selectedStaticReport === 'personal-performance') && 
        this.selectedFSRId) {
      params.fsrId = this.selectedFSRId;
    }

    return params;
  }

  initializeExportMenuItems() {
    this.exportMenuItems = [
      {
        label: 'Export as CSV',
        icon: 'pi pi-file',
        command: () => this.exportStaticReport('csv')
      },
      {
        label: 'Export as Excel',
        icon: 'pi pi-file-excel',
        command: () => this.exportStaticReport('excel')
      },
      {
        label: 'Export as PDF',
        icon: 'pi pi-file-pdf',
        command: () => this.exportStaticReport('pdf')
      }
    ];
  }

  togglePersonalPipelineType() {
    this.personalPipelineType = this.personalPipelineType === 'active' ? 'expired' : 'active';
    if (this.selectedStaticReport === 'personal-pipeline') {
      this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  onDateChange() {
    if (this.selectedStaticReport === 'personal-pipeline') {
      this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  toggleMonthlyWinLossType() {
    this.monthlyWinLossType = this.monthlyWinLossType === 'wins' ? 'losses' : 'wins';
    if (this.selectedStaticReport === 'monthly-winloss') {
      this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  onChangeMonth() {
    if (this.selectedStaticReport === 'leaderboard' || this.selectedStaticReport === 'monthly-winloss') {
      this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  onChangeYear() {
    if (this.selectedStaticReport === 'leaderboard' || this.selectedStaticReport === 'monthly-winloss') {
      this.loadStaticReportData(this.selectedStaticReport);
    }
  }

  goBackToReports() {
    this.selectedStaticReport = null;
    this.staticReportData = null;
  }

  getStaticReportName(reportId: string | null): string {
    if (!reportId) return '';
    const report = this.staticReports.find(r => r.id === reportId);
    return report?.name || '';
  }

  getStaticReportFrequency(reportId: string | null): string {
    if (!reportId) return '';
    const report = this.staticReports.find(r => r.id === reportId);
    return report?.frequency || '';
  }

  getMonthName(month: number): string {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return monthNames[month - 1] || '';
  }

  /**
   * Get formatted date labels for Team Activity report
   * Returns an array of date strings in format DD.MM.YY for the past 7 days
   */
  getTeamActivityDateLabels(): string[] {
    const labels: string[] = [];
    for (let i = 7; i >= 1; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      labels.push(`${day}.${month}.${year}`);
    }
    return labels;
  }
}

